/**
 * Plan-restructure experiment (CPD-25373) 10-row summary table content.
 * Row values mirror the yellow-highlighted rows in the source-of-truth sheet:
 * https://docs.google.com/spreadsheets/d/1E1maAuM9IrIs6S0mQrpXZC9CkgLk0JKDp1RHJZZNWGg
 */
export default {
  id: 'custom-plans',
  name: 'Plans',
  features: [
    {
      id: 'custom-plans',
      feature: {
        name: 'Plans',
        tooltipLabel: '',
      },
      parameters: [
        {
          id: '1',
          value: 'Lite',
        },
        {
          id: '2',
          value: 'Standard',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Premium',
        },
      ],
    },
    {
      id: 'custom-onboarding',
      feature: {
        name: 'Onboarding',
        tooltipLabel: "You'll speak 1:1 with our experts who will get you up and running in no time!",
      },
      parameters: [
        {
          id: '1',
          value: 'Live 1:1 onboarding with experts to help you get started fast',
        },
        {
          id: '2',
          value: 'Same as Lite',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Priority onboarding by dedicated team',
        },
      ],
    },
    {
      id: 'custom-liveSupport',
      feature: {
        name: 'Live Support',
        tooltipLabel:
          'An award-winning customer support team is readily available to help customers across all plans. Premium customers receive priority support with experienced agents.',
      },
      parameters: [
        {
          id: '1',
          value: 'Live phone and chat with marketing experts',
        },
        {
          id: '2',
          value: 'Same as Lite',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Dedicated priority support team',
        },
      ],
    },
    {
      id: 'custom-email',
      feature: {
        name: 'Email',
        tooltipLabel: 'Choose from hundreds of professionally designed customizable templates or create your own.',
      },
      parameters: [
        {
          id: '1',
          value:
            'Drag-and-drop email editor, AI copy generator, scheduled sends, subject line personalization, and dynamic content',
        },
        {
          id: '2',
          value: 'Same as Lite, plus unlimited subject line A/B testing and drilldown reporting',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard, plus email heat map',
        },
      ],
    },
    {
      id: 'custom-socialMediaMarketing',
      feature: {
        name: 'Social Media Marketing',
        tooltipLabel:
          'Start posting in minutes  — all in one place. Schedule content in a few clicks, reach new audiences with social posts, and turn followers into contacts.',
      },
      parameters: [
        {
          id: '1',
          value: 'Automatic posts on Facebook, Instagram, LinkedIn, and others to find new customers',
        },
        {
          id: '2',
          value: 'Schedule all social media posts in advance',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard',
        },
      ],
    },
    {
      id: 'custom-reporting',
      feature: {
        name: 'Reporting',
        tooltipLabel: 'See what’s working and what’s not with the ability to track your success in real time.',
      },
      parameters: [
        {
          id: '1',
          value: 'Reach and engagement reporting plus customer list growth and top-performing campaigns',
        },
        {
          id: '2',
          value: 'Same as Lite, plus drilldown reporting and email comparison',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard, plus email heat map',
        },
      ],
    },
    {
      id: 'custom-automation',
      feature: {
        name: 'Automation',
        tooltipLabel:
          'Set up easy automations to make sure your emails and texts go to the right person at the right time.',
      },
      parameters: [
        {
          id: '1',
          value: 'Unlimited templates, custom paths, and automated resend to non-openers (up to 100 runs/month)',
        },
        {
          id: '2',
          value: 'Same as Lite with 1,000 runs/month, plus AI campaign builder',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard with unlimited runs, plus ecommerce templates and auto-send welcome emails',
        },
      ],
    },
    {
      id: 'custom-customerRelationshipManagement',
      feature: {
        name: 'Customer Relationship Management',
        tooltipLabel:
          'Add and manage contacts in your account. You can manually add, import a list, or create an automatic way a contact gets added to your list. Every email includes a SafeUnsubscribe® link assuring your customers that you have used a legitimate permission-based email provider.',
      },
      parameters: [
        {
          id: '1',
          value: 'Up to 5,000 contacts with easy management, web signup forms, 10 lists, and 2 segments',
        },
        {
          id: '2',
          value: 'No contact cap with 25 lists, 5 segments, and ecommerce segmentation',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'No contact cap with unlimited lists and segments',
        },
      ],
    },
    {
      id: 'custom-advertising',
      feature: {
        name: 'Advertising',
        tooltipLabel:
          'Boost awareness and traffic for your business with targeted display ads in Facebook and/or Instagram feeds.',
      },
      parameters: [
        {
          id: '1',
          value: false,
        },
        {
          id: '2',
          value: 'Powerful social media ads manager',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard, plus Facebook lookalike targeting, Google Ad Manager, and SEO recommendations',
        },
      ],
    },
    {
      id: 'custom-events',
      feature: {
        name: 'Events',
        tooltipLabel:
          'Create, promote, and manage your event, all in one place. Enable tickets, add promo codes, and set up payment methods as part of the registration and payment process.',
      },
      parameters: [
        {
          id: '1',
          value: 'Host events, sell products, and take payments',
        },
        {
          id: '2',
          value: 'Host events, sell products, and take payments',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Host events, sell products, and take payments',
        },
      ],
    },
    {
      id: 'custom-integratedApps',
      feature: {
        name: 'Integrated Apps',
        tooltipLabel:
          'With over 300+ integrations, easily add your favorite apps to Constant Contact and sync data, simplify workflows, and amplify your marketing impact.',
      },
      parameters: [
        {
          id: '1',
          value:
            'Up to 3 integrations from our library including Google, Microsoft, Facebook, Canva, Shopify, Eventbrite and more',
        },
        {
          id: '2',
          value:
            'Unlimited integrations including Google, Microsoft, Facebook, Canva, LinkedIn, Vimeo, Zapier, Shopify, Eventbrite and more',
          variant: 'recommended' as const,
        },
        {
          id: '3',
          value: 'Same as Standard, plus enterprise integrations like Salesforce and Microsoft Dynamics',
        },
      ],
    },
  ],
};
